import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/context/AuthContext";
import { Loader, Currency, Field } from "@/components/Atoms";
import { UserSearchInput } from "@/components/admin/UserSearchInput";
import {
  Shield, Users, Crown, Coins, Settings, Wrench, Ban, ScrollText, Map as MapIcon,
  UserCog, Layers, Gift, PackageOpen, Server, Lock, KeyRound, Flame, Calendar,
  Flag, Sparkles, RefreshCw
} from "lucide-react";



export function Economy() {
  const [uid, setUid] = useState("");
  const [amt, setAmt] = useState("");
  const [op, setOp] = useState("set");
  const [fl, setFl] = useState({ discord_id: "", steam_id: "", server_name: "" });
  const toast = useToast();
  const submit = async (e) => {
    e.preventDefault();
    try {
      const ep = op === "set" ? "/admin/economy/set-points"
        : op === "add" ? "/admin/economy/add-points"
        : "/admin/economy/remove-points";
      const { data } = await api.post(ep, { user_id: uid, amount: parseInt(amt, 10) });
      toast.success(`New balance: ${data.new_balance}`);
    } catch (e) { toast.error(apiError(e)); }
  };
  const forceLink = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/admin/economy/forcelink", {
        discord_id: parseInt(fl.discord_id, 10),
        steam_id: fl.steam_id,
        server_name: fl.server_name,
      });
      if (data.fallback_used) {
        toast.error(`Linked with warning: ${data.warning}`);
      } else {
        toast.success(`Linked ${data.player_name} → ${data.player_uid}`);
      }
      setFl({ discord_id: "", steam_id: "", server_name: "" });
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="grid lg:grid-cols-2 gap-6 fade-up">
      <form onSubmit={submit} className="glass-card p-5 space-y-3" data-testid="admin-economy-form">
        <h3 className="label">Adjust Balance</h3>
        <Field label="User" hint="Search Discord display name or in-game name — admin types fewer characters this way.">
          <UserSearchInput
            value={uid}
            onChange={setUid}
            onPick={(row) => setUid(row.discord_id || "")}
            scope="discord_id"
            placeholder="Search Discord/in-game name or paste a user_id…"
            dataTestid="economy-user-search"
          />
        </Field>
        <Field label="Amount"><input className="input" required type="number" value={amt} onChange={(e) => setAmt(e.target.value)} data-testid="economy-amount" /></Field>
        <div className="grid grid-cols-3 gap-2">
          <button type="button" onClick={() => setOp("set")} className={`btn btn-sm ${op === "set" ? "" : "btn-secondary"}`} data-testid="economy-op-set">Set</button>
          <button type="button" onClick={() => setOp("add")} className={`btn btn-sm ${op === "add" ? "" : "btn-secondary"}`} data-testid="economy-op-add">Add</button>
          <button type="button" onClick={() => setOp("remove")} className={`btn btn-sm ${op === "remove" ? "btn-danger" : "btn-secondary"}`} data-testid="economy-op-remove">Remove</button>
        </div>
        <button className={`btn w-full ${op === "remove" ? "btn-danger" : ""}`} data-testid="economy-submit">Apply</button>
      </form>

      <form onSubmit={forceLink} className="glass-card p-5 space-y-3" data-testid="force-link-form">
        <h3 className="label">Force Link (Discord → Server)</h3>
        <p className="text-xs text-[var(--muted)] -mt-1">
          Mirrors the bot's <code>/economyset forcelink</code>. Resolves the player UID from <code>players</code>/<code>players_extended</code> by Steam/Platform ID.
        </p>
        <Field label="Discord User" hint="Typeahead also accepts a raw Discord ID.">
          <UserSearchInput
            value={fl.discord_id}
            onChange={(v) => setFl((s) => ({ ...s, discord_id: v }))}
            onPick={(row) => setFl((s) => ({
              ...s,
              discord_id: row.discord_id || "",
              server_name: row.server_name || s.server_name,
            }))}
            scope="discord_id"
            placeholder="Search Discord/in-game name…"
            dataTestid="forcelink-user-search"
          />
        </Field>
        <Field label="Steam / Platform ID"><input className="input font-mono text-xs" required placeholder="steam_76561198… or ps5_…" value={fl.steam_id} onChange={(e) => setFl({ ...fl, steam_id: e.target.value })} data-testid="forcelink-steam-id" /></Field>
        <Field label="Server name"><input className="input" required value={fl.server_name} onChange={(e) => setFl({ ...fl, server_name: e.target.value })} data-testid="forcelink-server" /></Field>
        <button className="btn" data-testid="forcelink-submit">Force Link</button>
      </form>
    </div>
  );
}
